// lib/api.ts - API Client for Laravel Backend

const normalizeBaseUrl = (url: string | undefined) => {
  const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000';
  const base = (url || fallbackOrigin || 'http://localhost:8000').replace(/\/$/, '');
  return base;
};

const normalizePrefix = (prefix: string | undefined) => {
  const value = prefix ?? '/api';
  const cleaned = value.startsWith('/') ? value : `/${value}`;
  return cleaned.replace(/\/$/, '');
};

const API_URL = `${normalizeBaseUrl(import.meta.env.VITE_API_URL)}${normalizePrefix(
  import.meta.env.VITE_API_PREFIX
)}`;

// API responses from Laravel come directly (not wrapped in { data: T })
// This type represents that the response IS the data itself, with optional message/errors
type ApiResponse<T = unknown> = T & {
  message?: string;
  errors?: Record<string, string[]>;
};

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const { params, ...fetchOptions } = options;
    
    let url = `${this.baseUrl}${endpoint}`;
    
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...((fetchOptions.headers as Record<string, string>) || {}),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          this.setToken(null);
          // Only redirect if not already on login/register page to prevent infinite loop
          if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
            window.location.href = '/login';
          }
        }
        throw { status: response.status, ...data };
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // GET request
  get<T>(endpoint: string, params?: Record<string, string>) {
    return this.request<T>(endpoint, { method: 'GET', params });
  }

  // POST request
  post<T>(endpoint: string, body?: any) {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  // PUT request
  put<T>(endpoint: string, body?: any) {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  // PATCH request
  patch<T>(endpoint: string, body?: any) {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  // DELETE request
  delete<T>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // File upload
  async upload<T>(endpoint: string, file: File, field = 'file') {
    const formData = new FormData();
    formData.append(field, file);

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        ...(this.token ? { 'Authorization': `Bearer ${this.token}` } : {}),
      },
      body: formData,
      credentials: 'include',
    });

    return response.json() as Promise<ApiResponse<T>>;
  }
}

// Create singleton instance
export const api = new ApiClient(API_URL);

// ===========================================
// AUTH API
// ===========================================
export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ user: any; token: string }>('/auth/login', { email, password }),

  register: (data: {
    email: string;
    password: string;
    password_confirmation: string;
    first_name: string;
    last_name: string;
  }) => api.post<{ user: any; token: string }>('/auth/register', data),

  logout: () => api.post('/auth/logout'),

  me: () => api.get<{ user: any }>('/auth/me'),

  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (data: {
    email: string;
    token: string;
    password: string;
    password_confirmation: string;
  }) => api.post('/auth/reset-password', data),

  updateProfile: (data: any) => api.put('/auth/profile', data),

  updatePassword: (data: {
    current_password: string;
    password: string;
    password_confirmation: string;
  }) => api.put('/auth/password', data),
};

// ===========================================
// SCHOOLS API
// ===========================================
export const schoolsApi = {
  list: () => api.get<{ schools: any[] }>('/schools'),
  get: (id: string) => api.get<{ school: any }>(`/schools/${id}`),
  update: (id: string, data: any) => api.put(`/schools/${id}`, data),
  getStats: (id: string) => api.get(`/schools/${id}/stats`),
  getUsers: (id: string, params?: { role?: string; search?: string }) =>
    api.get<{ users: any[]; meta: any }>(`/schools/${id}/users`, params),
  inviteUser: (
    schoolId: string,
    data: { email: string; role: 'teacher' | 'student' | 'admin'; first_name?: string; last_name?: string }
  ) => api.post<{ message: string; user: any }>(`/schools/${schoolId}/users/invite`, data),
};

// ===========================================
// USERS API
// ===========================================
export const usersApi = {
  list: (params?: { role?: string; search?: string }) =>
    api.get<{ users: any[]; meta: any }>('/users', params),
  get: (id: string) => api.get<{ user: any }>(`/users/${id}`),
  create: (data: any) => api.post('/users', data),
  update: (id: string, data: any) => api.put(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
};

// ===========================================
// COURSES API
// ===========================================
export const coursesApi = {
  list: (params?: { status?: string }) =>
    api.get<{ courses: any[] }>('/courses', params),
  get: (id: string) => api.get<{ course: any }>(`/courses/${id}`),
  create: (data: any) => api.post('/courses', data),
  update: (id: string, data: any) => api.put(`/courses/${id}`, data),
  delete: (id: string) => api.delete(`/courses/${id}`),
  
  // Enrollments
  getEnrollments: (courseId: string) =>
    api.get<{ enrollments: any[] }>(`/courses/${courseId}/enrollments`),
  enroll: (courseId: string, userId: string, role = 'student') =>
    api.post(`/courses/${courseId}/enrollments`, { user_id: userId, role }),
  unenroll: (courseId: string, userId: string) =>
    api.delete(`/courses/${courseId}/enrollments/${userId}`),
};

// ===========================================
// MEETINGS API
// ===========================================
export const meetingsApi = {
  list: (params?: { course_id?: string; status?: string; upcoming?: boolean; meeting_type?: string }) =>
    api.get<{
      meetings: Array<{
        id: string;
        title: string;
        description: string | null;
        room_name: string;
        meeting_type: 'lesson' | 'meeting' | 'event' | 'office_hours';
        scheduled_start: string;
        scheduled_end: string | null;
        actual_start: string | null;
        actual_end: string | null;
        status: 'scheduled' | 'live' | 'ended';
        recording_enabled: boolean;
        auto_recording: boolean;
        recording_status: string | null;
        host: { id: string; name: string } | null;
        course: { id: string; name: string } | null;
        school: { id: string; name: string } | null;
      }>;
      meta: { current_page: number; last_page: number; total: number };
    }>('/meetings', params as Record<string, string>),

  get: (id: string) => api.get<{ meeting: any }>(`/meetings/${id}`),

  create: (data: {
    school_id: string;
    course_id?: string;
    title: string;
    description?: string;
    meeting_type?: 'lesson' | 'meeting' | 'event' | 'office_hours';
    scheduled_start: string;
    scheduled_end?: string;
    recording_enabled?: boolean;
    auto_recording?: boolean;
    max_participants?: number;
    waiting_room_enabled?: boolean;
    chat_enabled?: boolean;
    screen_share_enabled?: boolean;
  }) => api.post<{ meeting: any }>('/meetings', data),

  update: (id: string, data: any) => api.put<{ meeting: any }>(`/meetings/${id}`, data),
  delete: (id: string) => api.delete<{ message: string }>(`/meetings/${id}`),

  // LiveKit - Join meeting
  join: (meetingId: string) =>
    api.get<{
      token: string;
      ws_url: string;
      room_name: string;
      is_host: boolean;
      meeting: any;
    }>(`/meetings/${meetingId}/join`),

  // Meeting lifecycle
  start: (meetingId: string) =>
    api.post<{ meeting: any }>(`/meetings/${meetingId}/start`),

  end: (meetingId: string) =>
    api.post<{ meeting: any }>(`/meetings/${meetingId}/end`),

  // Recording controls
  startRecording: (meetingId: string) =>
    api.post<{ message: string; meeting: any }>(`/meetings/${meetingId}/recording/start`),

  stopRecording: (meetingId: string) =>
    api.post<{ message: string; meeting: any }>(`/meetings/${meetingId}/recording/stop`),

  // Participants
  getParticipants: (meetingId: string) =>
    api.get<{
      live_participants: Array<{ identity: string; name: string; is_speaking: boolean }>;
      all_participants: Array<{
        id: string;
        name: string;
        role: string;
        joined_at: string | null;
        left_at: string | null;
        duration: number | null;
      }>;
    }>(`/meetings/${meetingId}/participants`),

  removeParticipant: (meetingId: string, identity: string) =>
    api.delete<{ message: string }>(`/meetings/${meetingId}/participants/${identity}`),

  // Attendance (legacy)
  getAttendance: (meetingId: string) =>
    api.get<{ attendance: any[] }>(`/meetings/${meetingId}/attendance`),
  markAttendance: (meetingId: string, userId: string, status: string) =>
    api.post(`/meetings/${meetingId}/attendance`, { user_id: userId, status }),
};

// ===========================================
// RECORDINGS API
// ===========================================
export const recordingsApi = {
  list: (params?: { status?: string; course_id?: string }) =>
    api.get<{
      recordings: Array<{
        id: string;
        title: string;
        meeting_title: string;
        duration: number;
        file_size: number;
        status: 'processing' | 'ready' | 'failed';
        created_at: string;
        meeting: { id: string; title: string } | null;
        course: { id: string; name: string } | null;
        recorded_by: { id: string; name: string } | null;
      }>;
    }>('/recordings', params as Record<string, string>),

  get: (recordingId: string) =>
    api.get<{
      recording: {
        id: string;
        title: string;
        description: string | null;
        duration: number;
        file_size: number;
        status: string;
        meeting: any;
        course: any;
      };
    }>(`/recordings/${recordingId}`),

  update: (recordingId: string, data: { title?: string; description?: string }) =>
    api.put<{ recording: any }>(`/recordings/${recordingId}`, data),

  delete: (recordingId: string) =>
    api.delete<{ message: string }>(`/recordings/${recordingId}`),

  getStreamUrl: (recordingId: string) =>
    api.get<{ url: string; expires_at: string }>(`/recordings/${recordingId}/stream`),

  getDownloadUrl: (recordingId: string) =>
    api.get<{ url: string; expires_at: string }>(`/recordings/${recordingId}/stream`),

  updateProgress: (recordingId: string, data: { position: number; completed?: boolean }) =>
    api.post(`/recordings/${recordingId}/progress`, data),
};

// ===========================================
// ASSIGNMENTS API
// ===========================================
export const assignmentsApi = {
  list: (courseId: string) =>
    api.get<{ assignments: any[] }>(`/courses/${courseId}/assignments`),
  get: (id: string) => api.get<{ assignment: any }>(`/assignments/${id}`),
  create: (courseId: string, data: any) =>
    api.post(`/courses/${courseId}/assignments`, data),
  update: (id: string, data: any) => api.put(`/assignments/${id}`, data),
  delete: (id: string) => api.delete(`/assignments/${id}`),
  
  // Submissions
  getSubmissions: (assignmentId: string) =>
    api.get<{ submissions: any[] }>(`/assignments/${assignmentId}/submissions`),
  submit: (assignmentId: string, data: any) =>
    api.post(`/assignments/${assignmentId}/submissions`, data),
  grade: (submissionId: string, data: { grade: number; feedback?: string }) =>
    api.put(`/submissions/${submissionId}/grade`, data),
};

// ===========================================
// LIBRARY API
// ===========================================
export const libraryApi = {
  // List files with filters
  list: (params?: {
    scope?: 'personal' | 'school' | 'course' | 'shared' | 'starred' | 'trash';
    folder_id?: string;
    type?: string;
    search?: string;
    course_id?: string;
    sort_by?: string;
    sort_dir?: string;
  }) =>
    api.get<{
      files: Array<{
        id: string;
        name: string;
        description: string | null;
        file_type: string;
        mime_type: string;
        file_size: number;
        is_folder: boolean;
        is_starred: boolean;
        is_trashed: boolean;
        cloud_source: string | null;
        url: string | null;
        thumbnail_url: string | null;
        download_count: number;
        view_count: number;
        created_at: string;
        updated_at: string;
        uploader: { id: string; name: string } | null;
        owner: { type: string; id: string; name: string } | null;
        parent_folder: { id: string; name: string } | null;
        share_permission?: string | null;
      }>;
      folder: { id: string; name: string; path: string[] } | null;
      meta: { current_page: number; last_page: number; total: number };
    }>('/library', params as Record<string, string>),

  get: (id: string) => api.get<{ file: any }>(`/library/${id}`),

  // Create folder or upload file
  upload: (file: File | null, data: {
    scope?: 'personal' | 'school' | 'course';
    course_id?: string;
    parent_folder_id?: string;
    name?: string;
    description?: string;
    is_folder?: boolean;
  }) => {
    const formData = new FormData();
    if (file) {
      formData.append('file', file);
    }
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });

    return fetch(`${API_URL}/library`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: formData,
    }).then(res => res.json());
  },

  update: (id: string, data: { name?: string; description?: string; parent_folder_id?: string }) =>
    api.put<{ file: any; message: string }>(`/library/${id}`, data),

  delete: (id: string) => api.delete<{ message: string }>(`/library/${id}`),

  restore: (id: string) => api.post<{ file: any; message: string }>(`/library/${id}/restore`),

  toggleStar: (id: string) =>
    api.post<{ file: any; is_starred: boolean }>(`/library/${id}/star`),

  copy: (id: string, data: { parent_folder_id?: string; name?: string }) =>
    api.post<{ file: any; message: string }>(`/library/${id}/copy`, data),

  download: (id: string) => {
    window.open(
      `${API_URL}/library/${id}/download?token=${localStorage.getItem('auth_token')}`,
      '_blank'
    );
  },

  // Sharing
  share: (id: string, data: {
    type: 'user' | 'email' | 'link';
    user_id?: string;
    email?: string;
    permission?: 'viewer' | 'commenter' | 'editor';
    link_password?: string;
    expires_at?: string;
    max_access_count?: number;
  }) => api.post<{ share: any; link?: string; message: string }>(`/library/${id}/share`, data),

  getShares: (id: string) =>
    api.get<{
      shares: Array<{
        id: string;
        type: string;
        permission: string;
        user?: { id: string; name: string; email: string };
        email?: string;
        link_token?: string;
        expires_at: string | null;
        access_count: number;
        created_at: string;
      }>;
    }>(`/library/${id}/shares`),

  unshare: (fileId: string, shareId: string) =>
    api.delete<{ message: string }>(`/library/${fileId}/shares/${shareId}`),

  accessSharedLink: (token: string, password?: string) =>
    api.get<{ file: any; download_url?: string }>(`/shared/${token}`, password ? { password } : undefined),

  // Attachments
  attach: (id: string, data: { attachable_type: string; attachable_id: string }) =>
    api.post<{ attachment: any; message: string }>(`/library/${id}/attach`, data),

  detach: (id: string, data: { attachable_type: string; attachable_id: string }) =>
    api.delete<{ message: string }>(`/library/${id}/attach`),

  // Activity log
  getActivity: (id: string) =>
    api.get<{
      activities: Array<{
        action: string;
        user: { id: string; name: string };
        details: any;
        ip_address: string;
        created_at: string;
      }>;
    }>(`/library/${id}/activity`),

  // Quota and stats
  getQuota: () =>
    api.get<{
      quota: {
        used_bytes: number;
        used_formatted: string;
        limit_bytes: number | null;
        limit_formatted: string | null;
        percentage: number;
        by_type: Record<string, { count: number; size: number }>;
      };
    }>('/library/quota'),

  getStats: () =>
    api.get<{
      stats: {
        total_files: number;
        total_size: number;
        total_size_formatted: string;
        by_type: Record<string, number>;
        recent_uploads: any[];
      };
    }>('/library/stats'),
};

// ===========================================
// CLOUD STORAGE API (Google Drive, OneDrive)
// ===========================================
export const cloudStorageApi = {
  // Connections
  getConnections: () =>
    api.get<{
      connections: Array<{
        id: string;
        provider: 'google_drive' | 'onedrive' | 'dropbox';
        provider_email: string;
        sync_enabled: boolean;
        auto_sync: boolean;
        status: string;
        storage_used: number;
        storage_limit: number;
        last_synced_at: string | null;
        last_error: string | null;
      }>;
    }>('/cloud-storage/connections'),

  getAuthUrl: (provider: 'google_drive' | 'onedrive' | 'dropbox') =>
    api.post<{ auth_url: string }>('/cloud-storage/auth-url', { provider }),

  disconnect: (connectionId: string) =>
    api.delete<{ message: string }>(`/cloud-storage/connections/${connectionId}`),

  // File operations
  listFiles: (connectionId: string, params?: { folder_id?: string; limit?: number }) =>
    api.get<{
      files: Array<{
        id: string;
        name: string;
        mime_type: string;
        size: number;
        is_folder: boolean;
        created_time: string;
        modified_time: string;
        thumbnail_url: string | null;
        web_view_url: string | null;
        download_url: string | null;
      }>;
      next_page_token: string | null;
    }>(`/cloud-storage/connections/${connectionId}/files`, params as Record<string, string>),

  searchFiles: (connectionId: string, query: string, limit?: number) =>
    api.get<{
      files: Array<{
        id: string;
        name: string;
        mime_type: string;
        size: number;
        is_folder: boolean;
        modified_time: string;
      }>;
    }>(`/cloud-storage/connections/${connectionId}/search`, { query, ...(limit && { limit: String(limit) }) }),

  getRecentFiles: (connectionId: string) =>
    api.get<{ files: any[] }>(`/cloud-storage/connections/${connectionId}/recent`),

  getSharedWithMe: (connectionId: string) =>
    api.get<{ files: any[] }>(`/cloud-storage/connections/${connectionId}/shared`),

  getQuota: (connectionId: string) =>
    api.get<{
      quota: {
        used: number;
        limit: number;
        remaining?: number;
      };
    }>(`/cloud-storage/connections/${connectionId}/quota`),

  sync: (connectionId: string) =>
    api.post<{
      message: string;
      result: { uploaded: number; downloaded: number; deleted: number; errors: any[] };
      connection: any;
    }>(`/cloud-storage/connections/${connectionId}/sync`),

  // Import/Export
  importFile: (connectionId: string, data: {
    file_id: string;
    folder_id?: number;
    library_type?: 'personal' | 'school';
  }) => api.post<{ message: string; file: any }>(`/cloud-storage/connections/${connectionId}/import`, data),

  exportFile: (connectionId: string, data: {
    file_id: string;
    folder_id?: string;
  }) => api.post<{ message: string; cloud_file_id: string }>(`/cloud-storage/connections/${connectionId}/export`, data),
};

// ===========================================
// EVENTS API
// ===========================================
export const eventsApi = {
  list: (params?: { start?: string; end?: string; type?: string; course_id?: string }) =>
    api.get<{
      events: Array<{
        id: string;
        title: string;
        description: string | null;
        type: string;
        start_at: string;
        end_at: string;
        all_day: boolean;
        location: string | null;
        color: string;
        course: { id: string; name: string } | null;
        creator: { id: string; name: string } | null;
        has_video_meeting: boolean;
        meeting: {
          id: string;
          room_name: string;
          status: string;
          recording_enabled: boolean;
          auto_recording: boolean;
        } | null;
      }>;
    }>('/events', params as Record<string, string>),

  get: (id: string) => api.get<{ event: any }>(`/events/${id}`),

  create: (data: {
    school_id: string;
    course_id?: string;
    title: string;
    description?: string;
    type: 'lesson' | 'class' | 'meeting' | 'event' | 'office_hours' | 'break' | 'holiday';
    start_at: string;
    end_at: string;
    all_day?: boolean;
    location?: string;
    color?: string;
    create_video_meeting?: boolean;
  }) => api.post<{ event: any }>('/events', data),

  update: (id: string, data: any) => api.put<{ event: any }>(`/events/${id}`, data),

  delete: (id: string) => api.delete<{ message: string }>(`/events/${id}`),
};

// ===========================================
// SCHEDULE API (Combined calendar view)
// ===========================================
export const scheduleApi = {
  // Get schedule for date range
  get: (params: { start: string; end: string; type?: string; course_id?: string }) =>
    api.get<{
      schedule: Array<{
        id: string;
        source: 'event' | 'meeting';
        type: string;
        title: string;
        description: string | null;
        start: string;
        end: string | null;
        all_day: boolean;
        location: string | null;
        color: string;
        course: { id: string; name: string } | null;
        creator: { id: string; name: string } | null;
        has_video_meeting: boolean;
        meeting: {
          id: string;
          room_name: string;
          status: string;
          recording_enabled: boolean;
          auto_recording: boolean;
        } | null;
      }>;
      range: { start: string; end: string };
    }>('/schedule', params as Record<string, string>),

  // Get today's schedule
  today: () =>
    api.get<{
      schedule: any[];
      range: { start: string; end: string };
    }>('/schedule/today'),

  // Get weekly schedule
  week: (date?: string) =>
    api.get<{
      schedule: any[];
      range: { start: string; end: string };
    }>('/schedule/week', date ? { date } : undefined),
};

// ===========================================
// CALENDAR API (Deprecated - use eventsApi/scheduleApi)
// ===========================================
export const calendarApi = {
  getEvents: (params: { start: string; end: string }) =>
    api.get<{ events: any[] }>('/events', params),
  createEvent: (data: any) => api.post('/events', data),
  updateEvent: (id: string, data: any) => api.put(`/events/${id}`, data),
  deleteEvent: (id: string) => api.delete(`/events/${id}`),
};

// ===========================================
// CALENDAR SYNC API (Google, Microsoft)
// ===========================================
export const calendarSyncApi = {
  // Connections
  getConnections: () =>
    api.get<{
      connections: Array<{
        id: string;
        provider: 'google' | 'microsoft';
        provider_email: string;
        calendar_id: string | null;
        calendar_name: string | null;
        sync_enabled: boolean;
        sync_to_external: boolean;
        sync_from_external: boolean;
        last_synced_at: string | null;
        sync_status: string;
        sync_error: string | null;
      }>;
    }>('/calendar/connections'),

  getAuthUrl: (provider: 'google' | 'microsoft') =>
    api.post<{ auth_url: string }>('/calendar/auth-url', { provider }),

  updateConnection: (connectionId: string, data: {
    calendar_id?: string;
    sync_enabled?: boolean;
    sync_to_external?: boolean;
    sync_from_external?: boolean;
  }) => api.put<{ connection: any }>(`/calendar/connections/${connectionId}`, data),

  disconnect: (connectionId: string) =>
    api.delete<{ message: string }>(`/calendar/connections/${connectionId}`),

  sync: (connectionId: string) =>
    api.post<{ message: string; synced_events: number; connection: any }>(`/calendar/connections/${connectionId}/sync`),

  getCalendars: (connectionId: string) =>
    api.get<{
      calendars: Array<{
        id: string;
        name: string;
        primary: boolean;
      }>;
    }>(`/calendar/connections/${connectionId}/calendars`),

  // Delegation
  getDelegations: () =>
    api.get<{
      delegations: Array<{
        id: number;
        delegate: { id: string; name: string; email: string };
        permissions: {
          can_view: boolean;
          can_create: boolean;
          can_edit: boolean;
          can_delete: boolean;
          can_respond: boolean;
        };
        created_at: string;
      }>;
    }>('/calendar/delegations'),

  addDelegation: (data: {
    delegate_user_id: string;
    can_view?: boolean;
    can_create?: boolean;
    can_edit?: boolean;
    can_delete?: boolean;
    can_respond?: boolean;
  }) => api.post<{ message: string; delegation: any }>('/calendar/delegations', data),

  removeDelegation: (delegationId: number) =>
    api.delete<{ message: string }>(`/calendar/delegations/${delegationId}`),
};

// ===========================================
// COURSE CALENDAR API
// ===========================================
export const courseCalendarApi = {
  getSettings: (courseId: string) =>
    api.get<{
      settings: {
        schedule_pattern: { days: number[]; time: string } | null;
        lesson_duration_minutes: number;
        course_start_date: string | null;
        course_end_date: string | null;
        total_lessons: number | null;
        auto_generate_events: boolean;
        auto_create_meetings: boolean;
        events_generated: boolean;
        sync_to_student_calendars: boolean;
        sync_to_teacher_calendars: boolean;
      };
    }>(`/courses/${courseId}/calendar/settings`),

  updateSettings: (courseId: string, settings: {
    schedule_pattern?: { days: number[]; time: string };
    lesson_duration_minutes?: number;
    course_start_date?: string;
    course_end_date?: string;
    total_lessons?: number;
    auto_generate_events?: boolean;
    auto_create_meetings?: boolean;
    sync_to_student_calendars?: boolean;
    sync_to_teacher_calendars?: boolean;
  }) => api.put<{ message: string }>(`/courses/${courseId}/calendar/settings`, settings),

  generateEvents: (courseId: string, data: {
    start_date: string;
    end_date?: string;
    total_lessons?: number;
    schedule_pattern: { days: number[]; time: string };
    lesson_duration_minutes?: number;
    create_meetings?: boolean;
  }) => api.post<{
    message: string;
    events: Array<{
      id: string;
      title: string;
      start_at: string;
      end_at: string;
      has_meeting: boolean;
    }>;
    total: number;
  }>(`/courses/${courseId}/calendar/generate`, data),

  clearEvents: (courseId: string, deleteMeetings?: boolean) =>
    api.delete<{ message: string; deleted: number }>(`/courses/${courseId}/calendar/clear${deleteMeetings !== undefined ? `?delete_meetings=${deleteMeetings}` : ''}`),
};

// ===========================================
// AI TEACHER API
// ===========================================
export const aiApi = {
  chat: (message: string, context?: { courseId?: string; conversationId?: string }) =>
    api.post<{ response: string; conversationId: string }>('/ai/chat', {
      message,
      ...context,
    }),
  getConversations: () => api.get<{ conversations: any[] }>('/ai/conversations'),
  getConversation: (id: string) =>
    api.get<{ conversation: any; messages: any[] }>(`/ai/conversations/${id}`),
};

// ===========================================
// DASHBOARD API
// ===========================================
export const dashboardApi = {
  getStats: (schoolId?: string) =>
    api.get<{
      stats: {
        students: { total: number; new_this_month: number };
        teachers: { total: number; new_this_month: number };
        courses: { total: number; active: number; online: number };
        meetings: { total: number; upcoming: number; live: number };
        attendance: { monthly: number; daily: number };
      };
      school: { id: string; name: string };
    }>('/dashboard/stats', schoolId ? { school_id: schoolId } : undefined),

  getCalendar: (date?: string, schoolId?: string) =>
    api.get<{
      today: Array<{
        id: string;
        title: string;
        course: string | null;
        start: string;
        end: string;
        status: string;
        type: string;
      }>;
      weekly: Array<{
        day: string;
        events: Array<{ id: string; title: string; time: string; status: string }>;
      }>;
      date: string;
    }>('/dashboard/calendar', {
      ...(date && { date }),
      ...(schoolId && { school_id: schoolId }),
    }),

  getNotifications: (schoolId?: string) =>
    api.get<{
      notifications: Array<{
        id: string;
        type: string;
        title: string;
        message: string;
        time: string;
      }>;
    }>('/dashboard/notifications', schoolId ? { school_id: schoolId } : undefined),

  getFinance: (schoolId?: string) =>
    api.get<{
      chart_data: Array<{ label: string; income: number; expenses: number }>;
      summary: { total_income: number; total_expenses: number; net: number };
    }>('/dashboard/finance', schoolId ? { school_id: schoolId } : undefined),
};

// ===========================================
// NOTIFICATIONS API
// ===========================================
export const notificationsApi = {
  list: (params?: { unread?: boolean; type?: string; category?: string; per_page?: number }) =>
    api.get<{
      notifications: Array<{
        id: string;
        type: string;
        category: string;
        title: string;
        body: string;
        data: any;
        action_url: string | null;
        action_text: string | null;
        icon: string;
        read: boolean;
        read_at: string | null;
        created_at: string;
        time_ago: string;
      }>;
      meta: {
        current_page: number;
        last_page: number;
        total: number;
        unread_count: number;
      };
    }>('/notifications', params as Record<string, string>),

  getUnreadCount: () =>
    api.get<{ unread_count: number }>('/notifications/unread-count'),

  markAsRead: (id: string) =>
    api.post<{ notification: any }>(`/notifications/${id}/read`),

  markAllAsRead: () =>
    api.post<{ message: string }>('/notifications/read-all'),

  delete: (id: string) =>
    api.delete<{ message: string }>(`/notifications/${id}`),

  clearRead: () =>
    api.delete<{ message: string }>('/notifications/clear-read'),
};

// ===========================================
// MESSAGES API
// ===========================================
export const messagesApi = {
  // Conversations
  getConversations: (params?: { per_page?: number }) =>
    api.get<{
      conversations: Array<{
        id: string;
        type: 'direct' | 'group';
        name: string | null;
        last_message_at: string | null;
        last_message_preview: string | null;
        unread_count: number;
        is_muted: boolean;
        recipient?: {
          id: string;
          name: string;
          avatar: string | null;
        };
        participants?: Array<{
          id: string;
          name: string;
          avatar: string | null;
          is_admin: boolean;
        }>;
      }>;
      meta: {
        current_page: number;
        last_page: number;
        total: number;
      };
    }>('/messages/conversations', params as Record<string, string>),

  getConversation: (id: string, params?: { per_page?: number }) =>
    api.get<{
      conversation: any;
      messages: Array<{
        id: string;
        content: string;
        attachments: any[];
        is_edited: boolean;
        is_deleted: boolean;
        created_at: string;
        sender: {
          id: string;
          name: string;
          avatar: string | null;
        };
        reply_to?: {
          id: string;
          content: string;
          sender_name: string;
        };
      }>;
      meta: {
        current_page: number;
        last_page: number;
        total: number;
      };
    }>(`/messages/conversations/${id}`, params as Record<string, string>),

  startDirect: (data: { recipient_id: string; school_id: string; message: string; attachments?: any[] }) =>
    api.post<{
      conversation: any;
      message: any;
    }>('/messages/conversations/direct', data),

  createGroup: (data: { name: string; participant_ids: string[]; school_id: string }) =>
    api.post<{ conversation: any }>('/messages/conversations/group', data),

  markAsRead: (conversationId: string) =>
    api.post<{ message: string }>(`/messages/conversations/${conversationId}/read`),

  toggleMute: (conversationId: string) =>
    api.post<{ is_muted: boolean }>(`/messages/conversations/${conversationId}/mute`),

  leave: (conversationId: string) =>
    api.post<{ message: string }>(`/messages/conversations/${conversationId}/leave`),

  // Messages
  sendMessage: (conversationId: string, data: { content: string; attachments?: any[]; reply_to_id?: string }) =>
    api.post<{ message: any }>(`/messages/conversations/${conversationId}/messages`, data),

  editMessage: (messageId: string, content: string) =>
    api.put<{ message: any }>(`/messages/messages/${messageId}`, { content }),

  deleteMessage: (messageId: string) =>
    api.delete<{ message: string }>(`/messages/messages/${messageId}`),

  // Utilities
  getUnreadCount: () =>
    api.get<{ unread_count: number }>('/messages/unread-count'),

  searchUsers: (query: string, schoolId: string) =>
    api.get<{
      users: Array<{
        id: string;
        name: string;
        email: string;
        avatar: string | null;
      }>;
    }>('/messages/search-users', { query, school_id: schoolId }),
};

// ===========================================
// PAYMENT PLANS API
// ===========================================
export const paymentPlansApi = {
  list: () =>
    api.get<{
      plans: Array<{
        id: string;
        name: string;
        type: 'single' | 'bundle' | 'subscription' | 'yearly' | 'term';
        price: number;
        currency: string;
        interval: 'one-time' | 'monthly' | 'yearly';
        additional_fees: Array<{ name: string; amount: number }>;
        free_trial: boolean;
        trial_days: number;
        active: boolean;
        courses_count: number;
        students_count: number;
        created_at: string;
      }>;
    }>('/plans'),

  get: (id: string) =>
    api.get<{ plan: any }>(`/plans/${id}`),

  create: (data: {
    name: string;
    type: 'single' | 'bundle' | 'subscription' | 'yearly' | 'term';
    price: number;
    currency?: string;
    interval: 'one-time' | 'monthly' | 'yearly';
    additional_fees?: Array<{ name: string; amount: number }>;
    free_trial?: boolean;
    trial_days?: number;
    active?: boolean;
  }) => api.post<{ plan: any }>('/plans', data),

  update: (id: string, data: any) =>
    api.put<{ plan: any }>(`/plans/${id}`, data),

  delete: (id: string) =>
    api.delete<{ message: string }>(`/plans/${id}`),
};

// ===========================================
// SETTINGS API (School Admin)
// ===========================================
export const settingsApi = {
  // Get all settings
  getAll: () =>
    api.get<{
      settings: {
        general: Record<string, any>;
        livekit: Record<string, any>;
        email: Record<string, any>;
        integrations: Record<string, any>;
        branding: Record<string, any>;
        notifications: Record<string, any>;
      };
      school: { id: string; name: string };
    }>('/settings'),

  // Get settings by category
  getCategory: (category: string) =>
    api.get<{
      category: string;
      settings: Record<string, any>;
    }>(`/settings/${category}`),

  // Update settings for a category
  updateCategory: (category: string, settings: Record<string, any>) =>
    api.put<{
      message: string;
      category: string;
      settings: Record<string, any>;
    }>(`/settings/${category}`, { settings }),

  // Update school info
  updateSchoolInfo: (data: {
    name?: string;
    description?: string;
    address?: string;
    phone?: string;
    email?: string;
    website?: string;
  }) =>
    api.put<{
      message: string;
      school: any;
    }>('/settings/school/info', data),

  // Upload branding asset
  uploadBranding: (type: 'logo' | 'favicon', file: File) => {
    const formData = new FormData();
    formData.append('type', type);
    formData.append('file', file);

    return fetch(`${API_URL}/settings/branding/upload`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: formData,
    }).then(res => res.json());
  },

  // Test email settings
  testEmail: () =>
    api.post<{ success: boolean; message: string }>('/settings/test/email'),

  // Test LiveKit connection
  testLivekit: () =>
    api.post<{ success: boolean; message: string }>('/settings/test/livekit'),
};

export default api;
